/**
 * setupFeeStatus — THE single frontend predicate for the setup-fee obligation
 * (plan W4, built 2026-08-20 after every surface grew its own copy and they
 * disagreed on screen: obligation said paid while the charge-derived button
 * still offered payment).
 *
 * The BACKEND owns the vocabulary (D-S8): `setup_status` is the obligation
 * (`owed | paid | exempt`; missing row = owed) and `has_pending_setup_fee`
 * says whether an emitted charge is open. Nothing here recomputes standing —
 * these are read-side projections of those two fields, used by:
 *   - MyFranchisesTable (Assinar / Pagar implantação conditions)
 *   - MyFranchisesPage (pending-card rows)
 *   - useSubscriptionStatus (the setup_fee_owed banner)
 *   - SubscribeForm (franchise eligibility)
 *
 * Deploy-window rule: a NULL/absent `setup_status` (older API) reads as
 * satisfied only when no charge is pending — the page stays usable and the
 * backend still refuses for real on submit.
 */

export interface SetupFeeRowLike {
  setup_status?: string | null
  has_pending_setup_fee?: boolean
}

/** Obligation satisfied: subscribing is allowed, no setup banner, no pay door. */
export function isSetupSatisfied(row: SetupFeeRowLike): boolean {
  if (row.setup_status === 'paid' || row.setup_status === 'exempt') return true
  if (row.setup_status == null || row.setup_status === '') {
    return row.has_pending_setup_fee !== true
  }
  return false
}

/** The banner/blocking projection — exactly the complement of satisfied. */
export function isSetupOwed(row: SetupFeeRowLike): boolean {
  return !isSetupSatisfied(row)
}

/**
 * A charge is open AND the obligation is not satisfied → offer the pay door.
 * Requiring BOTH makes "Assinar" and "Pagar implantação" mutually exclusive
 * by construction, even against stale data (obligation paid while a pending
 * charge row lingers can never show both again).
 */
export function hasPayableSetupCharge(row: SetupFeeRowLike): boolean {
  return row.has_pending_setup_fee === true && !isSetupSatisfied(row)
}
